import { useDispatch, useSelector } from "react-redux"
import { useMutation } from "@tanstack/react-query"
import { setAuth, setToken, setUser, setEmail, clearAuth } from "@/lib/store/auth"
import type { AppDispatch, RootState } from "@/lib/store"
import { Account } from "@/types/api"
import { forgotPassword, resetPasswordConfirm, resetPasswordChange } from "@/lib/http/auth"
import { useNavigate } from "react-router-dom"
import { useOnOrganizationEvent } from "./useOrganization"

export const useAuth = () => {
  const { token, user, email, loading } = useSelector((state: RootState) => state.auth)
  return { token, user, email, loading }
}

export const useOnAuthSuccess = () => {
  const navigate = useNavigate()
  const setAuth = useSetAuth()
  const onOrganizationEvent = useOnOrganizationEvent()

  return (data: Account) => {
    setAuth({ token: data.token, user: data })

    // Check for pending invitation
    const pendingInvitation = localStorage.getItem("pendingInvitation")
    if (pendingInvitation) {
      localStorage.removeItem("pendingInvitation")
      navigate(`/invite/${pendingInvitation}`, { replace: true })
      return
    }

    onOrganizationEvent()
  }
}

export const useSetAuth = () => {
  const dispatch = useDispatch<AppDispatch>()

  return (authData: { token: string; user: Account }) => {
    dispatch(setAuth(authData))
  }
}

export const useSetToken = () => {
  const dispatch = useDispatch<AppDispatch>()

  return (token: string) => {
    dispatch(setToken(token))
  }
}

export const useSetUser = () => {
  const dispatch = useDispatch<AppDispatch>()

  return (user: Account) => {
    dispatch(setUser(user))
  }
}

export const useSetEmail = () => {
  const dispatch = useDispatch<AppDispatch>()

  return (email: string) => {
    dispatch(setEmail(email))
  }
}

export const useClearAuth = () => {
  const dispatch = useDispatch<AppDispatch>()

  return () => {
    dispatch(clearAuth())
  }
}

export const useForgotPassword = (options: { onSuccess?: () => void; onError?: (error: any) => void }) =>
  useMutation({
    mutationFn: (email: string) => forgotPassword(email),
    onSuccess: options.onSuccess,
    onError: options.onError,
  })

export const useResetPasswordConfirm = (options: { onSuccess?: () => void; onError?: (error: any) => void }) =>
  useMutation({
    mutationFn: ({ email, token }: { email: string; token: string }) => resetPasswordConfirm(email, token),
    onSuccess: options.onSuccess,
    onError: options.onError,
  })

export const useResetPasswordChange = (options: { onSuccess?: () => void; onError?: (error: any) => void }) =>
  useMutation({
    mutationFn: ({ email, token, newPassword }: { email: string; token: string; newPassword: string }) =>
      resetPasswordChange(email, token, newPassword),
    onSuccess: options.onSuccess,
    onError: options.onError,
  })
